import React, {
  Children,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { captureException } from '@sentry/browser';
import cn from 'classnames';
import {
  FieldArray,
  FieldArrayRenderProps,
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
} from 'formik';

import {
  FitnessApplicationActivityType,
  PageType,
} from '../../../../types/__generated__';
import { usePageMeta } from '../../../hooks';
import { useCreateTimelineEntry } from '../../../hooks/timeline/useCreateTimelineEntry';
import { useApp } from '../../../hooks/useApp';
import { getMeasurementSystemFallback } from '../../../utils/measurementSystem';
import Button from '../../Button/Button';
import { useFitnessManagerContext } from '../../FitnessManager/FitnessManagerContext';
import FormDatePicker from '../../FormElements/FormDatePicker/FormikFormDatePicker';
import FormField from '../../FormElements/FormField/FormikFormField';
import FormSelect from '../../FormElements/FormSelect/FormikFormSelect';
import FormTextArea from '../../FormElements/FormTextArea/FormikFormTextArea';
import InlineFormContainer from '../../FormElements/InlineFormContainer/InlineFormContainer';
import { ImageGrid } from '../../ImageGrid';
import IsLargeScreen from '../../IsLargeScreen/IsLargeScreen';
import IsSmallOrMediumScreen from '../../IsSmallOrMediumScreen/IsSmallOrMediumScreen';
import MediaUploadSection from '../../Timeline/MediaUploadSection/MediaUploadSection';
import { TimelineImage } from '../../Timeline/timelineTypes';
import { UnitOfMeasure, toMeters } from './Helpers//ConversionHelpers';
import { durationToSeconds, hasDuration } from './Helpers/DurationHelpers';
import {
  isNonNegativeNumber,
  numericRangeClamp,
} from './Helpers/NumberHelpers';
import {
  getActivityTypes,
  getTimePeriods,
  getUnitsOfMeasure,
} from './Helpers/SelectItemBuilders';
import {
  TimePeriod,
  formatTimeFromDate,
  isValidPartialTimeValue,
  parseDateTime,
} from './Helpers/TimeHelpers';
import {
  ManualFitnessEntryFormErrors,
  buildValidator,
} from './Helpers/Validation';
import {
  ManualFitnessEntryFormContext,
  useManualFitnessEntryFormContext,
} from './ManualFitnessEntryFormContext';

import styles from './ManualFitnessEntryForm.scss';

export interface ManualFitnessEntryFormValues {
  title: string;
  activityType?: FitnessApplicationActivityType;
  distanceUnit: UnitOfMeasure;
  distance?: number;
  elevation?: number;
  elevationUnit: UnitOfMeasure;
  durationHours?: number;
  durationMinutes?: number;
  durationSeconds?: number;
  steps?: number;
  activityDate: Date;
  activityTime: string;
  activityTimePeriod: TimePeriod;
  notes?: string;
  media: TimelineImage[];
}

export interface ManualFitnessEntryFormProps {
  children: ReactNode;
  title: string;
  activities: FitnessApplicationActivityType[];
}

export const ManualFitnessEntryForm = ({
  children,
  title,
  activities,
}: ManualFitnessEntryFormProps) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { hideToast } = useApp();
  const activityValues = useActivityValues(activities);
  const formik = useFormik(children, activityValues);
  const { modalManager } = useFitnessManagerContext();

  const initialValues: ManualFitnessEntryFormValues = {
    title: '',
    activityType: undefined,
    distanceUnit: activityValues.distanceUnits.find(u => u.isSelected)!.value,
    distance: undefined,
    elevation: undefined,
    elevationUnit: activityValues.elevationUnits.find(u => u.isSelected)!.value,
    durationHours: undefined,
    durationMinutes: undefined,
    durationSeconds: undefined,
    steps: undefined,
    notes: undefined,
    activityDate: activityValues.now,
    activityTime: formatTimeFromDate(activityValues.now),
    activityTimePeriod:
      activityValues.now.getHours() < 12 ? TimePeriod.AM : TimePeriod.PM,
    media: [],
  };

  useEffect(() => {
    hideToast();
  }, [hideToast]);

  return (
    <div className={styles.manualFitnessEntryModal}>
      <IsSmallOrMediumScreen>
        <h1>{title}</h1>
      </IsSmallOrMediumScreen>
      <IsLargeScreen>
        <h3>{title}</h3>
      </IsLargeScreen>
      <Formik
        onSubmit={formik.onSubmit}
        validate={formik.onValidate}
        initialValues={initialValues}
      >
        {({
          isValid,
          isSubmitting,
          values,
          touched,
          errors,
        }: FormikProps<ManualFitnessEntryFormValues> & {
          errors: ManualFitnessEntryFormErrors;
        }) => {
          const activityDateTimeTouched =
            touched.activityDate ||
            touched.activityTime ||
            touched.activityTimePeriod;
          const activityDateTimeError =
            errors.activityDate ??
            errors.activityTime ??
            errors.activityTimePeriod;
          const durationTouched =
            touched.durationHours ||
            touched.durationMinutes ||
            touched.durationSeconds;
          const durationError =
            errors.duration ??
            errors.durationHours ??
            errors.durationMinutes ??
            errors.durationSeconds;
          const distanceTouched = touched.distance;
          const distanceError = errors.distance ?? errors.distanceUnit;
          const elevationTouched = touched.elevation;
          const elevationError = errors.elevation ?? errors.elevationUnit;

          return (
            <ManualFitnessEntryFormContext
              touched={{
                activityDateTimeTouched: Boolean(activityDateTimeTouched),
                durationTouched,
                distanceTouched,
                elevationTouched,
              }}
              errors={{
                activityDateTimeError,
                durationError,
                distanceError,
                elevationError,
              }}
              values={values}
              callbacks={{
                onImageUploaded: formik.onImageUploaded,
                onImageUploadedFailure: formik.onImageUploadedFailure,
              }}
              activityValues={activityValues}
            >
              <Form>
                {children}
                <Button
                  disabled={isSubmitting || !isValid}
                  type="submit"
                  className="jg-space-mbsm"
                >
                  {isSubmitting ? (
                    <Loader size="small" colour="white" />
                  ) : (
                    t('manualFitness.formSubmit')
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={modalManager.actions.onCloseModal}
                  className={cn('jg-btn--ghost', 'jg-space-mbsm')}
                >
                  {t('manualFitness.formCancel')}
                </Button>
              </Form>
            </ManualFitnessEntryFormContext>
          );
        }}
      </Formik>
    </div>
  );
};

function useActivityValues(activities: FitnessApplicationActivityType[]) {
  const [now] = useState(new Date(Date.now()));
  const { t } = useTranslation('common', { useSuspense: false });
  const { settings } = useFitnessManagerContext();

  const definedMeasurementSystem =
    settings.measurementSystem ?? getMeasurementSystemFallback();

  const timePeriods = useMemo(() => getTimePeriods(t, now), [t, now]);

  const activityTypes = useMemo(() => getActivityTypes(t, activities), [
    t,
    activities,
  ]);

  const distanceUnits = useMemo(
    () => getUnitsOfMeasure(t, 'distance', definedMeasurementSystem),
    [t, definedMeasurementSystem],
  );

  const elevationUnits = useMemo(
    () => getUnitsOfMeasure(t, 'elevation', definedMeasurementSystem),
    [t, definedMeasurementSystem],
  );

  return {
    timePeriods,
    activityTypes,
    distanceUnits,
    elevationUnits,
    now,
  };
}

const useFormik = (
  children: ReactNode,
  activityValues: ReturnType<typeof useActivityValues>,
) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { showToast } = useApp();
  const [createTimelineEntry] = useCreateTimelineEntry();
  const { page } = useFitnessManagerContext();
  const { modalManager } = useFitnessManagerContext();
  const { pageSlug, pageType } = usePageMeta();

  const childFormSections = useMemo(
    () =>
      Children.map(children, child => {
        switch ((child as any).type) {
          case ActivityTypes:
            return 'ActivityTypes';
          case Steps:
            return 'Steps';
          default:
            return null;
        }
      }),
    [children],
  );

  const handleValidate = useMemo(
    () =>
      buildValidator(
        t,
        activityValues.activityTypes,
        activityValues.distanceUnits,
        activityValues.elevationUnits,
        activityValues.timePeriods,
        childFormSections,
      ),
    [
      t,
      activityValues.activityTypes,
      activityValues.distanceUnits,
      activityValues.elevationUnits,
      activityValues.timePeriods,
      childFormSections,
    ],
  );

  const handleSubmit = async (
    values: ManualFitnessEntryFormValues,
    { setSubmitting }: FormikHelpers<ManualFitnessEntryFormValues>,
  ) => {
    setSubmitting(true);

    // Hardcoding to walk for Fitbit manual activities. Form validation will pick up when the Activity Types
    // section is present and not populated, so we're safe to assume that if activityType is undefined we are
    // inside a Fitbit form.
    const activityType = values.activityType
      ? values.activityType
      : FitnessApplicationActivityType.Walk;

    const hours = values.durationHours;
    const minutes = values.durationMinutes;
    const seconds = values.durationSeconds;
    const duration = hasDuration(hours, minutes, seconds)
      ? durationToSeconds(hours, minutes, seconds)
      : undefined;
    const distance =
      values.distance && isNonNegativeNumber(values.distance)
        ? Math.round(toMeters(values.distanceUnit, values.distance))
        : undefined;
    const elevation =
      values.elevation &&
      isNonNegativeNumber(values.elevation) &&
      values.activityType !== FitnessApplicationActivityType.Swim
        ? Math.round(toMeters(values.elevationUnit, values.elevation))
        : undefined;
    const steps = values.steps ? values.steps : 0;

    try {
      await createTimelineEntry({
        type: pageType.toUpperCase() as PageType,
        pageId: page.legacyId,
        pageSlug: pageSlug,
        message: values.notes,
        createdDate: parseDateTime(
          values.activityDate,
          values.activityTime,
          values.activityTimePeriod,
        ).toJSON(),
        fitness: {
          title: values.title,
          activityType,
          distance,
          duration,
          elevation,
          steps,
        },
        media: values.media,
      });

      showToast({
        message: t('manualFitness.submitSuccess'),
        type: 'success',
        dismissAfter: 5000,
      });
    } catch (error) {
      showToast({
        message: t('manualFitness.submitFail'),
        type: 'warning',
        dismissAfter: 10000,
      });
      captureException(error);
    }

    setSubmitting(false);
    modalManager.actions.onCloseModal();
  };

  const handleImageUploaded = useCallback(
    (
      image: TimelineImage,
      values: ManualFitnessEntryFormValues,
      arrayHelpers: FieldArrayRenderProps,
    ) => {
      const index = values.media.findIndex(x => x.id === image.id);
      arrayHelpers.replace(index, image);
    },
    [],
  );

  const handleImageUploadFailure = useCallback(
    (
      image: TimelineImage,
      values: ManualFitnessEntryFormValues,
      arrayHelpers: FieldArrayRenderProps,
    ) => {
      const index = values.media.findIndex(x => x.id === image.id);
      if (index >= 0) {
        arrayHelpers.remove(index);
      }
    },
    [],
  );

  return {
    onValidate: handleValidate,
    onSubmit: handleSubmit,
    onImageUploaded: handleImageUploaded,
    onImageUploadedFailure: handleImageUploadFailure,
  };
};

export const DatePicker = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    touched: { activityDateTimeTouched },
    errors: { activityDateTimeError },
    activityValues,
  } = useManualFitnessEntryFormContext();

  const dateIsOutsideRange = (date: Date, now: Date) => {
    return date.setHours(0, 0, 0, 0) > now.setHours(0, 0, 0, 0);
  };

  return (
    <InlineFormContainer
      qaAttribute="manual-fitness-activity-date-time"
      touched={activityDateTimeTouched}
      error={activityDateTimeError}
    >
      <FormDatePicker
        id="activityDate"
        name="activityDate"
        label={t('manualFitness.activityDate.label')}
        numberOfMonths={1}
        isOutsideRange={(d: any) =>
          dateIsOutsideRange(
            ('toDate' in d ? d.toDate() : d) as Date,
            activityValues.now,
          )
        }
        isRequired={true}
        displayError={false}
        className={styles.activityDate}
      />

      <FormField
        id="activityTime"
        name="activityTime"
        label={t('manualFitness.activityTime.label')}
        placeholder={t('manualFitness.activityTime.placeholder')}
        isValidChange={isValidPartialTimeValue}
        isRequired={true}
        displayError={false}
        className={styles.activityTime}
      />

      <FormSelect
        name="activityTimePeriod"
        placeholder=""
        items={activityValues.timePeriods}
        displayError={false}
        className={styles.activityTimePeriod}
      />
    </InlineFormContainer>
  );
};

export const ActivityTypes = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { activityValues } = useManualFitnessEntryFormContext();

  return (
    <FormSelect
      name="activityType"
      label={t('manualFitness.activityType.label')}
      placeholder={t('manualFitness.activityType.placeholder')}
      items={activityValues.activityTypes}
      isRequired={true}
    />
  );
};

export const Distance = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    touched: { distanceTouched },
    errors: { distanceError },
    activityValues,
  } = useManualFitnessEntryFormContext();

  return (
    <InlineFormContainer
      qaAttribute="manual-fitness-distance"
      touched={distanceTouched}
      error={distanceError}
    >
      <FormField
        type="number"
        id="distance"
        name="distance"
        label={t('manualFitness.distance.label')}
        placeholder={t('manualFitness.distance.placeholder')}
        className={styles.numberOfMeasure}
        displayError={false}
        isValidChange={newVal => numericRangeClamp(newVal, 0, 999999)}
        step="any"
      />

      <FormSelect
        name="distanceUnit"
        label={t('manualFitness.distanceUnit.label')}
        placeholder=""
        items={activityValues.distanceUnits}
        className={styles.unitOfMeasure}
        displayError={false}
      />
    </InlineFormContainer>
  );
};

export const Elevation = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    touched: { elevationTouched },
    errors: { elevationError },
    values: { activityType },
    activityValues,
  } = useManualFitnessEntryFormContext();

  if (activityType === FitnessApplicationActivityType.Swim) return null;

  return (
    <InlineFormContainer
      qaAttribute="manual-fitness-elevation"
      touched={elevationTouched}
      error={elevationError}
    >
      <FormField
        type="number"
        id="elevation"
        name="elevation"
        label={t('manualFitness.elevation.label')}
        placeholder={t('manualFitness.elevation.placeholder')}
        className={styles.numberOfMeasure}
        displayError={false}
        isValidChange={newVal => numericRangeClamp(newVal, 0, 999999)}
        step="any"
      />

      <FormSelect
        name="elevationUnit"
        label={t('manualFitness.elevationUnit.label')}
        placeholder=""
        items={activityValues.elevationUnits}
        className={styles.unitOfMeasure}
        displayError={false}
      />
    </InlineFormContainer>
  );
};

export const Duration = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    touched: { durationTouched },
    errors: { durationError },
  } = useManualFitnessEntryFormContext();

  return (
    <InlineFormContainer
      qaAttribute="manual-fitness-duration"
      touched={durationTouched}
      error={durationError}
      className={styles.duration}
    >
      <FormField
        type="number"
        id="durationHours"
        name="durationHours"
        label={t('manualFitness.duration.label')}
        placeholder={t('manualFitness.duration.hours.placeholder')}
        className={styles.inlineField}
        displayError={false}
        isValidChange={newVal => numericRangeClamp(newVal, 0, 999)}
      >
        <label className={styles.inlineLabel}>
          {t('manualFitness.duration.hours.label')}
        </label>
      </FormField>

      <FormField
        type="number"
        id="durationMinutes"
        name="durationMinutes"
        placeholder={t('manualFitness.duration.minutes.placeholder')}
        className={styles.inlineField}
        displayError={false}
        isValidChange={newVal => numericRangeClamp(newVal, 0, 59)}
      >
        <label className={styles.inlineLabel}>
          {t('manualFitness.duration.minutes.label')}
        </label>
      </FormField>

      <FormField
        type="number"
        id="durationSeconds"
        name="durationSeconds"
        placeholder={t('manualFitness.duration.seconds.placeholder')}
        className={styles.inlineField}
        displayError={false}
        isValidChange={newVal => numericRangeClamp(newVal, 0, 59)}
      >
        <label className={styles.inlineLabel}>
          {t('manualFitness.duration.seconds.label')}
        </label>
      </FormField>
    </InlineFormContainer>
  );
};

export const Title = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <FormField
      id="title"
      name="title"
      label={t('manualFitness.title.label')}
      placeholder={t('manualFitness.title.placeholder')}
      isRequired={true}
    />
  );
};

export const Story = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <FormTextArea
      name="notes"
      label={t('manualFitness.notes.label')}
      placeholder={t('manualFitness.notes.placeholder')}
      maxLength={400}
      showCharacterCount={true}
      minRows={5}
    />
  );
};

export const ImageUpload = () => {
  const {
    values,
    callbacks: { onImageUploaded, onImageUploadedFailure },
  } = useManualFitnessEntryFormContext();

  return (
    <FieldArray name="media" validateOnChange={true}>
      {arrayHelpers => (
        <div className={styles.mediaUpload}>
          <ImageGrid
            images={(values.media || []).map(image => ({
              ...image,
              url: image.preview,
            }))}
            gridType="equal"
            onImageDelete={arrayHelpers.remove}
          />
          <MediaUploadSection
            submitSucceeded={false}
            onImageSelected={image => {
              arrayHelpers.unshift(image);
            }}
            onImageUploaded={(image: TimelineImage) =>
              onImageUploaded(image, values, arrayHelpers)
            }
            onImageUploadFailure={(image: TimelineImage) =>
              onImageUploadedFailure(image, values, arrayHelpers)
            }
          />
        </div>
      )}
    </FieldArray>
  );
};

export const Steps = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  return (
    <FormField
      type="number"
      id="steps"
      name="steps"
      label={t('manualFitness.steps.label')}
      placeholder={t('manualFitness.steps.placeholder')}
      displayError={true}
      isValidChange={newVal => numericRangeClamp(newVal, 1, 999999)}
      isRequired={true}
    />
  );
};

export default ManualFitnessEntryForm;
ManualFitnessEntryForm.DatePicker = DatePicker;
ManualFitnessEntryForm.ActivityTypes = ActivityTypes;
ManualFitnessEntryForm.Distance = Distance;
ManualFitnessEntryForm.Elevation = Elevation;
ManualFitnessEntryForm.Duration = Duration;
ManualFitnessEntryForm.Title = Title;
ManualFitnessEntryForm.Story = Story;
ManualFitnessEntryForm.ImageUpload = ImageUpload;
ManualFitnessEntryForm.Steps = Steps;
